import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, Typography, Link, TextField, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, List, ListItem, ListItemAvatar, ListItemText, Avatar, RadioGroup, Radio, FormControlLabel, } from '@mui/material';
import axios from 'axios';
import { useLocation, useHistory } from 'react-router-dom'; 
import logo from "../components/Logo/Colloquia_Logo.png";
import Tooltip from '@mui/material/Tooltip';

function USSenateElections() {
    const history = useHistory();
    const location = useLocation();
    
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [polls, setPolls] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios
            .get("/elections/USSenate/polls")
            .then(res => setPolls(res.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        axios.get("/user/me")
            .then(res => setLoggedInUser(res.data))
            .catch(() => setLoggedInUser(null));
    }, []);

    const ContentBox = ({ title, pollId, children }) => (
        <Box
            onClick={() => history.push(`/Government/Elections/USSenateElections/${pollId}/comments`)}
            p={2}
            my={2}
            border="1px solid #ccc"
            borderRadius="8px"
            sx={{
                position: "relative",
                maxWidth: 900,
                mx: "auto",
                my: 2,
                cursor: "pointer",
                    "&:hover": { 
                        backgroundColor: "#f9f9f9" 
                    },
            }}
        >
            <Typography 
                fontWeight="bold"
                sx={{
                    wordBreak: "break-word",
                    pr: "140px",
                    mb: 1
                }}
            >
                {title}
        </Typography>
            {children}
        </Box>
    );

    const visitProfile = (e, userId) => {
        e.stopPropagation(); // prevent opening the post
        history.push(`/user/view/${userId}`);
    };

    const VoteDialog = ({ open, onClose, options, pollId, onVoted }) => {
        const [selected, setSelected] = useState("");
        const [comment, setComment] = useState("");

        const handleSubmit = async () => {
            if (!selected || !comment.trim()) return;

            const commentRes = await axios.post(
                `/elections/USSenate/${pollId}/comment`,
                { text: comment }
            );

            const commentId = commentRes.data._id; 

            await axios.post(
                `/elections/USSenate/${pollId}/vote`,
                { optionId: selected, commentId }
            );

            const pollRes = await axios.get(`/elections/USSenate/singlePoll/${pollId}`);

            onVoted(pollRes.data);

            onClose();
        };

        return (
            <Dialog 
                open={open} 
                onClose={onClose} 
                onClick={(e) => e.stopPropagation()} 
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: "#FFFDF8",
                        borderRadius: 3,
                    },
                }}
            >
                <DialogTitle>Cast Your Power Vote</DialogTitle>

                <DialogContent>
                    <RadioGroup
                        value={selected}
                        onChange={(e) => setSelected(e.target.value)}
                    >
                        {options.map(opt => (
                            <FormControlLabel
                                key={opt.id}
                                value={opt.id}
                                control={<Radio />}
                                label={opt.label}
                            />
                        ))}
                    </RadioGroup>

                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Explain your power vote (required)"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        variant="outlined"
                        disabled={!comment || !selected}
                        onClick={handleSubmit}
                    >
                        Submit Power Vote
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const ResultRow = ({ item }) => (
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            py={1}
            borderBottom="1px solid #eee"
        >
            <Box>
                <Typography fontWeight={500}>{item.label}</Typography>
                {/* CLICKABLE VOTE COUNT */}
                <Typography
                    variant="caption"
                    color="primary"
                    //sx={{ cursor: "pointer", color: "primary.main", "&:hover": { textDecoration: "underline" } }}
                    //onClick={(e) => {
                    //    e.stopPropagation();
                    //    onVotesClick(item.id);
                    //}}
                >
                    {item.votes} Power Votes
                </Typography>
            </Box>

            <Typography fontWeight={500}>
                {item.percentage}%
            </Typography>
        </Box>
    );

    const VoteBox = ({ pollId, title }) => {
        const [poll, setPoll] = useState(null);
        const [open, setOpen] = useState(false);
        const [undoDialogOpen, setUndoDialogOpen] = useState(false);
        const [loading, setLoading] = useState(true);
        const [votersDialogOpen, setVotersDialogOpen] = useState(false);
        const [selectedOptionVoters, setSelectedOptionVoters] = useState([]);

        // Fetch single poll
        useEffect(() => {
            if (!pollId) return;

            setLoading(true);
            axios
                .get(`/elections/USSenate/singlePoll/${pollId}`)
                .then(res => setPoll(res.data))
                .catch(console.error)
                .finally(() => setLoading(false));
        }, [pollId]);

        if (loading || !poll || !poll.options) return null;

        const totalVotes = poll.options.reduce((sum, o) => sum + o.votes, 0);

        const sortedResults = [...poll.options]
            .sort((a, b) => b.votes - a.votes)
            .map(opt => ({
                ...opt,
                percentage: totalVotes
                    ? Math.round((opt.votes / totalVotes) * 100)
                    : 0,
        }));

        const votedOption = poll.options.find(o => o.id === poll.userVote);
        const votedOptionLabel = votedOption?.label;

        const undoVote = async () => {
            try {
                await axios.post(`/elections/USSenate/${poll._id}/undoVote`);
                const res = await axios.get(`/elections/USSenate/singlePoll/${poll._id}`);
                setPoll(res.data);
            } catch (err) {
                console.error("Undo vote failed", err);
            }
        };

        const showVotersForOption = (optionId) => {
            const voters = poll.votes.filter(v => v.optionId === optionId);
            setSelectedOptionVoters(voters);
            setVotersDialogOpen(true);
        };

        return (
            <ContentBox title={title} pollId={pollId}>
                {/* Centered Vote Button */}
                <Box
                    display="flex"
                    justifyContent="center"
                    mb={2}
                >
                    {poll.userVote ? (
                        <Box
                            sx={{
                                position: "relative",
                                display: "inline-flex",
                                alignItems: "center",
                                "&:hover .voted-btn": {
                                    opacity: 0,
                                },
                                "&:hover .undo-btn": {
                                    opacity: 1,
                                    pointerEvents: "auto",
                                },
                            }}
                        >
                            <Button
                                className="voted-btn"
                                variant="contained"
                                color="success"
                                disabled
                                sx={{
                                    transition: "opacity 0.2s ease",
                                }}
                            >
                                Voted for {votedOptionLabel}
                            </Button>

                            <Button
                                className="undo-btn"
                                variant="contained"
                                color="error"
                                sx={{
                                    inset: 0, 

                                    position: "absolute",
                                    transition: "opacity 0.2s ease",
                                    opacity: 0,
                                    pointerEvents: "none",
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setUndoDialogOpen(true);
                                }}
                            >
                                Undo Vote
                            </Button>
                        </Box>
                    ) : (
                        <Button
                            variant="outlined"
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpen(true);
                            }}
                        >
                            Power Vote
                        </Button>
                    )}
                </Box>

                {/* Results */}
                <Box>
                    {sortedResults.map(item => (
                        <ResultRow 
                            key={item.id} 
                            item={item} 
                            //onVotesClick={showVotersForOption}
                        />
                    ))}
                </Box>

                {/* Dialog */}
                <VoteDialog
                    open={open}
                    onClose={() => setOpen(false)}
                    options={poll.options}
                    pollId={poll._id}
                    onVoted={updatedPoll => setPoll(updatedPoll)}
                />

                {/* Undo Vote Dialog */}
                <Dialog
                    open={undoDialogOpen}
                    onClose={() => setUndoDialogOpen(false)}
                    onClick={(e) => e.stopPropagation()} 
                    PaperProps={{
                        sx: {
                            backgroundColor: "#FFFDF8",
                            borderRadius: 3,
                            px: 2,
                            py: 1.5,
                        },
                    }}
                    >
                    <DialogTitle sx={{ fontWeight: 600 }}>
                        Undo your vote?
                    </DialogTitle>

                    <DialogContent>
                        <DialogContentText sx={{ color: "text.primary" }}>
                            This will remove your vote and the vote rationale comment associated with it.
                            This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions sx={{ pb: 2, px: 3 }}>
                        <Button
                            onClick={() => setUndoDialogOpen(false)}
                            variant="outlined"
                            sx={{
                                borderColor: "#d0cfc8",
                                color: "text.primary",
                            }}
                        >
                            Cancel
                        </Button>

                        <Button
                            onClick={async () => {
                                await undoVote();      
                                setUndoDialogOpen(false);
                            }}    
                            variant="contained"
                            color="error"
                            sx={{
                                boxShadow: "none",
                            }}
                        >
                            Yes, Undo Vote
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* list of voters */}
                <Dialog
                    open={votersDialogOpen}
                    onClose={() => setVotersDialogOpen(false)}
                    onClick={(e) => e.stopPropagation()} 
                    fullWidth
                    maxWidth="xs"
                    PaperProps={{
                        sx: {
                            backgroundColor: "#FFFDF8",
                            borderRadius: 3,
                        },
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 600 }}>
                        Voters
                    </DialogTitle>

                    <DialogContent dividers>
                        {selectedOptionVoters.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No votes
                        </Typography>
                        ) : (
                        <List disablePadding>
                            {selectedOptionVoters.map(vote => (
                            <ListItem 
                                key={vote.user._id} 
                                button
                                onClick={(e) => visitProfile(e, vote.user._id)}
                                sx={{
                                    px: 0,
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s',
                                    '&:hover': {
                                        backgroundColor: '#FFF2CC', // subtle highlight
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                    }}
                                >
                                    {/* Left side: Avatar + user info */}
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <ListItemAvatar>
                                        <Avatar src={vote.user.profileImageUrl}>
                                            {vote.user.username?.[0]}
                                        </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={`${vote.user.first_name} ${vote.user.last_name}`}
                                            secondary={`@${vote.user.username}`}
                                        />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Voting Power: {vote.user.tier}
                                    </Typography>
                                </Box>
                            </ListItem>
                            ))}
                        </List>
                        )}
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={() => setVotersDialogOpen(false)}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </ContentBox>
        );
    };

    const navItems = [
      { label: "U.S. House of Representatives", path: "/Government/Elections/USHouseOfRepresentativesElections" },
      { label: "U.S. Senate", path: "/Government/Elections/USSenateElections" },
      { label: "State Governors", path: "/Government/Elections/GubernatorialElections" },
      { label: "State Legislatures", path: "/Government/Elections/StateLegislatureElections" },
      { label: "Statewide Offices", path: "/Government/Elections/StatewideOfficesElections" },
    ];

    const getButtonStyles = (path) => {
      const active = location.pathname === path;

      return {
        padding: "6px 12px",
        borderColor: active ? "#1f1f1f" : "#333333",
        color: active ? "#1f1f1f" : "#333333",
        backgroundColor: active ? "rgba(0,0,0,0.08)" : "transparent",
        fontWeight: active ? 600 : 500,
        transition: "all 0.15s ease",
        "&:hover": {
          borderColor: "black",
          color: "black",
          backgroundColor: "rgba(0,0,0,0.12)"
        }
      };
    };

    return (
        <div
           style={{
               padding: '20px',
               backgroundColor: '#FFFDF8',
               minHeight: '100vh',  // ensures it covers the full viewport height
               boxSizing: 'border-box', // ensures padding doesn't affect layout
           }}
       >
           <div style={{ width: '200px' }}></div>
           {/* Centered Buttons */}
           {/*<Box
               display="flex"
               justifyContent="center"
               alignItems="center"
               gap={2}
               flexWrap="wrap"
               sx={{
                   mt: 4,
                   mb: 4,
               }}
                   >
                   <Button
                       variant="outlined"
                       sx={{
                           padding: "6px 12px",
                           borderColor: "#333333", 
                           color: "#333333",      
                           backgroundColor: "transparent",
                           '&:hover': {
                               borderColor: "black",
                               color: "black",
                               backgroundColor: "rgba(0, 0, 0, 0.04)", // subtle hover background
                           },
                       }}
                   >
                       U.S. House of Representatives
                   </Button>
                   <Button
                       variant="outlined"
                       sx={{
                           padding: "6px 12px",
                           borderColor: "#333333", 
                           color: "#333333",      
                           backgroundColor: "transparent",
                           '&:hover': {
                               borderColor: "black",
                               color: "black",
                               backgroundColor: "rgba(0, 0, 0, 0.04)", // subtle hover background
                           },
                       }}
                   >
                       U.S. Senate
                   </Button>
                   <Button
                       variant="outlined"
                       sx={{
                           padding: "6px 12px",
                           borderColor: "#333333", 
                           color: "#333333",      
                           backgroundColor: "transparent",
                           '&:hover': {
                               borderColor: "black",
                               color: "black",
                               backgroundColor: "rgba(0, 0, 0, 0.04)", // subtle hover background
                           },
                       }}
                       onClick={() => history.push('/Government/Elections/GubernatorialElections')}
                   >
                       State Governors
                   </Button>
                   <Button
                       variant="outlined"
                       sx={{
                           padding: "6px 12px",
                           borderColor: "#333333", 
                           color: "#333333",      
                           backgroundColor: "transparent",
                           '&:hover': {
                               borderColor: "black",
                               color: "black",
                               backgroundColor: "rgba(0, 0, 0, 0.04)", // subtle hover background
                           },
                       }}
                   >
                       State Legislatures
                   </Button>
                   <Button
                       variant="outlined"
                       sx={{
                           padding: "6px 12px",
                           borderColor: "#333333", 
                           color: "#333333",      
                           backgroundColor: "transparent",
                           '&:hover': {
                               borderColor: "black",
                               color: "black",
                               backgroundColor: "rgba(0, 0, 0, 0.04)", // subtle hover background
                           },
                       }}
                   >
                       Statewide Offices
                   </Button>
              </Box>*/}
              {/* Sticky Navigation */}
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1000,
                  backgroundColor: "#FFFDF8",
                  //borderBottom: "1px solid rgba(0,0,0,0.08)",
                  py: 2
                }}
              >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        maxWidth: "100%",
                    }}
                >
                    {/* LEFT — LOGO */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            minWidth: 220, // keeps layout stable
                        }}
                    >
                        <Tooltip
                            title="Lady Wisdom"
                            arrow
                            followCursor
                        >
                            <Box
                                component="img"
                                src="/Colloquia_Logo.png"
                                alt="Colloquia Logo"
                                onClick={() => history.push("/home")}
                                sx={{
                                    height: 90,
                                    width: "auto",
                                    borderRadius: "10px", // rounded edges
                                    cursor: "pointer",
                                    objectFit: "contain",
                                }}
                            />
                        </Tooltip>
                    </Box>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={2}
                  flexWrap="wrap"
                >
                  {navItems.map((item) => (
                    <Button
                      key={item.path}
                      variant="outlined"
                      sx={getButtonStyles(item.path)}
                      onClick={() => history.push(item.path)}
                    >
                      {item.label}
                    </Button>
                  ))}
                </Box>
                <Box sx={{ minWidth: 220 }} />
              </Box>
              </Box>
              {polls.map(poll => (
                    <VoteBox
                        pollId={poll._id}
                        title={poll.title}
                    />
                ))}
        </div>
    );
}

export default USSenateElections;